/**
 * Valid strategy keys (7 options - matches backend constraint)
 */
export const VALID_STRATEGY_KEYS = [
  'trend',
  'scalping',
  'support_resistance',
  'candle_patterns',
  'swing',
  'chart_patterns',
  'fvg'
] as const;

export type StrategyKey = typeof VALID_STRATEGY_KEYS[number];

/**
 * Strategy options for dropdowns (key → display label)
 */
export const STRATEGIES: Array<{ key: StrategyKey; label: string }> = [
  { key: 'trend', label: 'Trend Following' },
  { key: 'scalping', label: 'Scalping' },
  { key: 'support_resistance', label: 'Support & Resistance' },
  { key: 'candle_patterns', label: 'Candle Patterns' },
  { key: 'swing', label: 'Swing Trading' },
  { key: 'chart_patterns', label: 'Chart Patterns' },
  { key: 'fvg', label: 'Fair Value Gap (FVG)' }
];

/**
 * Legacy mapping for backwards compatibility
 */
export const STRATEGY_LABELS: Record<string, string> = {
  trend: 'Trend Following',
  'trend-following': 'Trend Following',
  'trend_following': 'Trend Following',
  scalping: 'Scalping',
  'swing-trading': 'Swing Trading',
  swing_trading: 'Swing Trading',
  swing: 'Swing Trading',
  'support-resistance': 'Support & Resistance',
  support_resistance: 'Support & Resistance',
  'candle-patterns': 'Candle Patterns',
  candle_patterns: 'Candle Patterns',
  'chart-patterns': 'Chart Patterns',
  chart_patterns: 'Chart Patterns',
  fvg: 'Fair Value Gap (FVG)'
};

/**
 * Gets a clean strategy label from strategy or strategy_key
 * @param strategy Primary strategy field
 * @param strategyKey Alternative strategy key field
 * @returns Formatted strategy label
 */
export function getStrategyLabel(strategy?: string | null, strategyKey?: string | null): string {
  const key = (strategyKey || strategy || '').toLowerCase().trim();
  
  // Check exact match first
  if (key && STRATEGY_LABELS[key]) {
    return STRATEGY_LABELS[key];
  }

  // Get raw value for fallback formatting
  const raw = (strategy || strategyKey || '').trim();
  if (!raw) return '—';
  
  // If it's a simple key format (lowercase with underscores/hyphens), format it nicely
  if (/^[a-z0-9_-]+$/.test(raw)) {
    return raw
      .split(/[_-]/)
      .map(word => {
        // Keep short words (3 chars or less) uppercase, capitalize others
        if (word.length <= 3) {
          return word.toUpperCase();
        }
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }
  
  // Return as-is if already formatted
  return raw;
}

/**
 * Gets all available strategy options for dropdowns
 */
export function getStrategyOptions(): Array<{ value: string; label: string }> {
  return Object.entries(STRATEGY_LABELS).map(([value, label]) => ({
    value,
    label
  }));
}